import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { ColumnEntity } from '@/editor/entities/columnEntity';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { OperateAbstract } from './OperateAbstract';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { cloneDeep } from 'lodash-es';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { getAllStructOutlines } from '@/editor/stores/utils/column';
import { ColumnAbsorbManager } from '@/editor/manager/absorb/box/columnAbsorbManager';

export class OperateStruct extends OperateAbstract<EEntityType.Column> {
  private _checkedEntity: ColumnEntity | null | undefined = null;

  private _virtualColumnEntity: ColumnEntity | null = null;
  private _virtualColumnData: IColumnData | null = null;
  private _rulerModel = new RulerModel();
  private _structOutlines: { start: IPoint2d; end: IPoint2d }[] = [];
  private _columnAbsorbManager = new ColumnAbsorbManager();

  constructor() {
    super();
  }

  set checkedEntity(column: ColumnEntity | undefined | null) {
    if (!column) return;

    this._checkedEntity = column;
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  override startMove(_point: IPoint2d): void {
    this.moving = true;
    if (!this._checkedEntity) return;

    const columnData = globalStores.structStore.getColumnData(this._checkedEntity.id);
    if (!columnData) return;

    this._virtualColumnData = cloneDeep(columnData);
    this._virtualColumnEntity = new ColumnEntity(this._virtualColumnData);
    this._structOutlines = getAllStructOutlines();
    this._columnAbsorbManager.updateData();
  }

  move(point: IPoint2d) {
    this.moving = true;
    if (!this._checkedEntity || !this._virtualColumnData) return point;

    const columnData = globalStores.structStore.getColumnData(this._checkedEntity.id);
    if (!columnData) return point;

    const rotatedBox = {
      center: point,
      width: columnData.type === EColumnType.cylinder ? columnData.radius * 2 : columnData.width,
      thickness: columnData.type === EColumnType.cylinder ? columnData.radius * 2 : columnData.thickness,
      rotation: columnData.rotation
    };
    const absorbResult = this._columnAbsorbManager.calculateThenRender(rotatedBox);

    if (absorbResult) {
      this._virtualColumnData.center = absorbResult.absorbPoint;
      this._virtualColumnData.rotation = absorbResult.rotation;
    } else {
      this._virtualColumnData.center = point;
      this._virtualColumnData.rotation = columnData.rotation;
    }

    this.updateVirtualColumn();

    return this._virtualColumnData.center;
  }

  override moveByOffset(offset: IPoint2d) {
    if (!this._checkedEntity || !this._virtualColumnData) return;

    this.moving = true;

    this._virtualColumnData.center = Point2dUtils.added(this._virtualColumnData.center, offset);

    this.updateVirtualColumn();
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;

    if (!this._checkedEntity || !this._virtualColumnData) return;

    globalStores.structOperation.modifyColumn(this._checkedEntity.id, {
      center: this._virtualColumnData.center,
      rotation: this._virtualColumnData.rotation
    });

    this._virtualColumnData = null;
    this._virtualColumnEntity?.clear();
    this._rulerModel.clear();
    this._columnAbsorbManager.clearModel();
  }

  private updateVirtualColumn() {
    if (!this._virtualColumnData || !this._virtualColumnEntity) return;

    this._virtualColumnEntity.update(this._virtualColumnData);

    const [width, thickness] =
      this._virtualColumnData.type === EColumnType.squareColumn
        ? [this._virtualColumnData.width, this._virtualColumnData.thickness]
        : [this._virtualColumnData.radius * 2, this._virtualColumnData.radius * 2];
    const rulerInfos = calculateBoxRulerInfos(
      {
        center: this._virtualColumnData.center,
        width,
        thickness,
        rotation: this._virtualColumnData.rotation
      },
      this._structOutlines,
      true
    );

    this._rulerModel.updateModel(rulerInfos);
  }

  clear() {
    this.checkedEntity = null;
    this.moving = false;
    this._virtualColumnData = null;
    this._virtualColumnEntity?.clear();
    this._rulerModel.clear();
    this._columnAbsorbManager.clearModel();
  }

  dispose() {
    this.clear();

    this._virtualColumnEntity?.dispose();
    this._rulerModel.dispose();
    this._columnAbsorbManager.dispose();
  }
}
